import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosConfig";
import { formatDateTime } from "../utils/formatters";
import { isDevSite, useDevSiteOverrides } from "../utils/devSites";
import DevSiteSwitch from "../components/DevSiteSwitch";
import "./InstallationDetails.css";

const formatEventType = (type) => {
  if (!type) return "";

  const spaced = type.replace(/([a-z])([A-Z])/g, "$1 $2");

  return spaced
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const capitalizeFirstLetter = (text) => {
  if (!text || typeof text !== "string") return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatKey = (key) => {
  const customLabels = {
    id: "ID",
    instance_id: "Instance Id",
    instanceId: "Instance Id",
    url: "URL",
    app_installed: "App Installed",
  };

  if (customLabels[key]) return customLabels[key];

  let spaced = key.replace(/_/g, " ");
  spaced = spaced.replace(/([a-z])([A-Z])/g, "$1 $2");

  return spaced
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const InstallationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [installationData, setInstallationData] = useState(null);
  const [collectionsData, setCollectionsData] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const devSiteOverrides = useDevSiteOverrides();

  const activityWrapperRef = useRef(null);

  const scrollBoxes = (direction) => {
    if (!activityWrapperRef.current) return;
    const scrollAmount = 250;
    if (direction === "left") {
      activityWrapperRef.current.scrollLeft -= scrollAmount;
    } else {
      activityWrapperRef.current.scrollLeft += scrollAmount;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setError("Access denied. Please log in.");
        setLoading(false);
        return;
      }

      try {
        const installationRes = await axiosInstance.get(
          `/admin/installations/${id}`,
        );
        const installation = installationRes.data.installation;

        const instanceId = installation?.instanceId;
        if (!instanceId) {
          setError("InstanceId not found.");
          setLoading(false);
          return;
        }

        const [collectionsRes, activityRes] = await Promise.all([
          axiosInstance.get(`/collections/${id}`),
          axiosInstance
            .get(`/api/installation-activity?instanceId=${instanceId}`)
            .catch((err) => {
              if (err.response && err.response.status === 404) {
                console.warn(
                  `No activity logs found for instanceId: ${instanceId}`,
                );
                return { data: { data: [] } };
              }
              throw err;
            }),
        ]);

        setInstallationData(installation);
        setCollectionsData(collectionsRes.data);
        setActivityData(activityRes.data?.data || []);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (activityWrapperRef.current) {
      activityWrapperRef.current.scrollLeft =
        activityWrapperRef.current.scrollWidth;
    }
  }, [activityData]);

  const handleBackClick = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/installations");
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!installationData) return <div>No installation data found.</div>;

  return (
    <div className="installation-row">
      <div className="installation-container">
        <h2 className="installation-title">Installation Details</h2>
        <div className="installation-table-wrapper">
          <table className="installation-table">
            <tbody>
              {(() => {
                const entries = Object.entries(installationData);
                const ownerEmailIdx = entries.findIndex(
                  ([k]) => k === "owner_email",
                );
                const siteNameIdx = entries.findIndex(
                  ([k]) => k === "site_name",
                );
                if (
                  ownerEmailIdx !== -1 &&
                  siteNameIdx !== -1 &&
                  ownerEmailIdx !== siteNameIdx + 1
                ) {
                  const [ownerEmailEntry] = entries.splice(ownerEmailIdx, 1);
                  const newSiteNameIdx = entries.findIndex(
                    ([k]) => k === "site_name",
                  );
                  entries.splice(newSiteNameIdx + 1, 0, ownerEmailEntry);
                }
                return entries;
              })().map(([key, value]) => {
                if (key === "id") return null; // 🔥 Skip rendering the 'id' row

                if (value === null || value === undefined) {
                  return (
                    <tr key={key}>
                      <td className="nested-key">{formatKey(key)}</td>
                      <td className="nested-value">—</td>
                    </tr>
                  );
                }

                if (typeof value === "object" && !Array.isArray(value)) {
                  return Object.entries(value).map(([subKey, subValue]) => (
                    <tr key={`${key}-${subKey}`}>
                      <td className="nested-key">{formatKey(subKey)}</td>
                      <td className="nested-value">
                        {[
                          "installed_at",
                          "reinstalled_at",
                          "uninstalled_at",
                          "expires_on",
                        ].includes(subKey)
                          ? formatDateTime(subValue)
                          : subKey === "app_installed" ||
                              subKey === "auto_renew"
                            ? subValue === 1
                              ? "Yes"
                              : "No"
                            : subValue?.toString() || "—"}
                      </td>
                    </tr>
                  ));
                }

                return (
                  <tr key={key}>
                    <td className="nested-key">{formatKey(key)}</td>
                    <td className="nested-value">
                      {[
                        "installed_at",
                        "reinstalled_at",
                        "uninstalled_at",
                        "expires_on",
                        "plan_purchase_at",
                      ].includes(key) ? (
                        Array.isArray(value) ? (
                          value.map((v, i) => (
                            <div key={i}>{formatDateTime(v)}</div>
                          ))
                        ) : (
                          formatDateTime(value)
                        )
                      ) : ["app_installed", "auto_renew"].includes(
                          key.toLowerCase(),
                        ) ? (
                        value === 1 ? (
                          "Yes"
                        ) : (
                          "No"
                        )
                      ) : key.toLowerCase().includes("url") &&
                        typeof value === "string" ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#ffffff",
                            textDecoration: "underline",
                          }}
                        >
                          {value}
                        </a>
                      ) : value !== null && value !== undefined ? (
                        value.toString()
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className="nested-key">Dev Site</td>
                <td className="nested-value">
                  <DevSiteSwitch
                    installation={installationData}
                    checked={isDevSite(installationData, devSiteOverrides)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="collections-section">
        <h3 className="installation-title">Collections</h3>
        <div className="collection-box">
          {collectionsData?.collectionCount !== undefined && (
            <p>
              <strong>Collection Count:</strong>{" "}
              {collectionsData.collectionCount}
            </p>
          )}
          {collectionsData?.collectionIds?.length > 0 ? (
            <ul className="collections-list">
              {collectionsData.collectionIds.map((collectionId, idx) => (
                <li
                  key={idx}
                  className="clickable-collection"
                  onClick={() => navigate(`/collections/${id}/${collectionId}`)}
                >
                  {collectionId}
                </li>
              ))}
            </ul>
          ) : (
            <p>No collections data available.</p>
          )}
        </div>
      </div>

      <div className="activity-log-section">
        <h3 className="installation-title">Activity Logs</h3>
        <div className="activity-log-box small-box">
          <div className="scroll-buttons">
            <button
              className="scroll-btn left"
              onClick={() => scrollBoxes("left")}
            >
              &lt;
            </button>
            <button
              className="scroll-btn right"
              onClick={() => scrollBoxes("right")}
            >
              &gt;
            </button>
          </div>
          {activityData.length > 0 ? (
            <div className="activity-box-wrapper" ref={activityWrapperRef}>
              {[...activityData]
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((log, index) => {
                  let data;
                  try {
                    data =
                      typeof log.eventData === "string" &&
                      log.eventData.trim().startsWith("{")
                        ? JSON.parse(log.eventData)
                        : log.eventData;
                  } catch (e) {
                    data = log.eventData;
                  }

                  const displayData =
                    typeof data === "object" &&
                    (data?.operationTimeStamp || data?.appId)
                      ? null
                      : typeof data === "object"
                        ? JSON.stringify(data)
                        : data;

                  return (
                    <React.Fragment key={log.id}>
                      <div className="activity-box compact-box">
                        <p>{formatEventType(log.eventType)}</p>
                        <p>{formatDateTime(log.date)}</p>
                        {displayData && displayData !== "-" && (
                          <p>{capitalizeFirstLetter(displayData)}</p>
                        )}
                      </div>
                      {index < activityData.length - 1 && (
                        <div className="arrow-box">→</div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          ) : (
            <p>No activity logs available for this installation.</p>
          )}
        </div>
      </div>

      <div className="back-button-wrapper">
        <button className="back-btn" onClick={handleBackClick}>
          Back
        </button>
      </div>
    </div>
  );
};

export default InstallationDetails;
